import is from '@sindresorhus/is';

import type { ActiveEntity, EntityIdentifier, EntityType, FrontendContext } from '../data-contract/chat';
import type { ChatContext, JobProfileChatContext } from './chat-context-types';

const itemTypeToEntityType: Record<JobProfileChatContext['itemType'], EntityType> = {
    'job-level': 'level',
    'job-role': 'role',
};

const toEntityIdentifier = (context: ChatContext): EntityIdentifier => {
    switch (context.kind) {
        case 'job-profile':
            return {
                id: context.itemId,
                jobArchitectureId: context.architectureId,
                type: itemTypeToEntityType[context.itemType],
                version: is.number(context.version) ? context.version : undefined,
            };
        case 'jsa':
            return {
                clusterId: context.selectedClusterId,
                familyId: context.selectedFamilyId,
                id: context.architectureId,
                jobArchitectureId: context.architectureId,
                levelId: context.selectedLevelId,
                roleId: context.selectedRoleId,
                type: 'job_architecture',
            };
        case 'job-family':
            return {
                familyId: context.familyId,
                id: context.familyId,
                jobArchitectureId: context.architectureId,
                type: 'family',
            };
        case 'job-cluster':
            return {
                clusterId: context.clusterId,
                familyId: context.familyId,
                id: context.clusterId,
                jobArchitectureId: context.architectureId,
                type: 'cluster',
            };
        case 'skill':
            return {
                id: context.skillId,
                jobArchitectureId: context.architectureId,
                type: 'skill',
            };
    }
};

const toActiveEntity = (context: ChatContext): ActiveEntity => ({
    entity: toEntityIdentifier(context),
    mode: 'view',
});

export const chatContextsToFrontendContext = (
    contexts: readonly ChatContext[],
    url?: string,
): FrontendContext | undefined => {
    const activeEntities = contexts.map((context) => toActiveEntity(context));
    // The URL is registered as context no matter where the chat is opened, so a
    // context with only a URL (and no active entities) is still meaningful.
    if (activeEntities.length === 0 && is.undefined(url)) return undefined;
    return {
        activeEntities,
        ...(is.undefined(url) ? {} : { url }),
    };
};

export const chatContextToFrontendContext = (
    context: ChatContext | null,
    url?: string,
): FrontendContext | undefined => {
    return chatContextsToFrontendContext(is.null(context) ? [] : [context], url);
};
